import React, { FC, useEffect } from "react";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import CheckoutBrandingSection from "./CheckoutBrandingSection";
import CheckoutIFrame from "./CheckoutIFrame";
import {
  CheckoutDetailSection,
  CheckoutPaymentWrapper,
  ProductDetailWrapper,
} from "../../../shared/styles";
import {
  CollapsiblePanel,
  CreatableSelect,
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  ModalComponent,
  SapphireBlueButton,
  SingleSelect,
  ToastService,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { CheckOutStore } from "../../../shared/store";
import {
  CheckOutPromoCodeSelectionType,
  PaymentMethodTypes,
} from "../../../shared/enums";
import {
  AuthService,
  CountryService,
  DonationService,
} from "../../../shared/services";
import {
  CheckoutBrandingModel,
  CheckoutOptionsPageModel,
  CheckoutPaymentFormModel,
  CheckoutPaymentPageModel,
  DonationPricingPageInputsModel,
  ItemDetailInputModel,
  ListInterestTagModel,
} from "../../../shared/models";
import { ItemHeader } from "../../../shared/components";

type IProps = {
  checkoutStore: CheckOutStore;
  navigate: NavigateFunction;
};

const paymentMethods: IOption[] = Object.keys(PaymentMethodTypes).map((key) => {
  return {
    label: key,
    value: PaymentMethodTypes[key as keyof typeof PaymentMethodTypes],
  };
});

const CheckOutOptionsPage: FC<IProps> = (props: IProps) => {
  const { optionsPage, listInterestTagStore, paymentPageData } =
    props.checkoutStore;
  const { formData } = paymentPageData;

  useEffect(() => {
    if (props.checkoutStore.detailPage.isDonationOnly) {
      DonationService.getDonation(
        AuthService.authData.accountId,
        props.checkoutStore.sellPage.items[0].itemId || ""
      ).subscribe({
        next: (result) => {
          if (!result.result) {
            ToastService.showError("Error in loading donation details");
          } else {
            const detailSectionData = ItemDetailInputModel.deserializeAPIModel(
              result.result
            );
            const priceData =
              DonationPricingPageInputsModel.deserializeAPIModel(
                result.result.price
              );
            props.checkoutStore.updateDonationData(
              detailSectionData,
              priceData,
              true
            );
          }
        },
        error: () => {
          ToastService.showError("Error in loading product details");
        },
      });
    }
    loadCounties();
  }, []);

  const loadCounties = () => {
    CountryService.getCountriesWithState().subscribe((response) => {
      if (response.result) {
        const USCountry = response.result.find(
          (c) => c.abbreviationName === "US"
        );
        const brandingData = new CheckoutBrandingModel({
          ...optionsPage.brandingData,
          countryDetails: USCountry,
        });
        props.checkoutStore.updateOptionsPage(
          new CheckoutOptionsPageModel({
            ...optionsPage,
            brandingData: brandingData,
          })
        );
      }
    });
  };

  const handleInputChange = <K extends keyof CheckoutOptionsPageModel>(
    field: K,
    value: CheckoutOptionsPageModel[K]
  ) => {
    const clonedData = new CheckoutOptionsPageModel(optionsPage);
    clonedData[field] = value;
    props.checkoutStore.updateOptionsPage(clonedData);
  };

  const selectedContactOption = (list: ListInterestTagModel[]): IOption[] => {
    if (!list) return [];
    return list.map((item) => {
      return {
        label: item.name,
        value: item.id,
      };
    });
  };

  const handleSelectChange = async (
    field: keyof CheckoutOptionsPageModel,
    newValue: IOption | null
  ): Promise<void> => {
    if (!newValue) {
      handleInputChange(field, []);
      return;
    }
    if (newValue.__isNew__) {
      const type =
        field === "selectedList"
          ? "List"
          : field === "selectedTag"
            ? "Tag"
            : "";
      if (!type) return;
      const listId = await listInterestTagStore.createNew(newValue.label, type);
      if (!listId) return;
      newValue.value = listId;
    }

    switch (field) {
      case "selectedList":
      case "selectedTag":
        optionsPage[field] = [];
        if (!optionsPage[field]?.find((c) => c.id === newValue.value)) {
          optionsPage[field].push(
            new ListInterestTagModel({
              id: newValue.value,
              name: newValue.label,
            })
          );
        }
        break;
    }
    handleInputChange(
      field,
      optionsPage[field as keyof CheckoutOptionsPageModel]
    );
  };

  const renderListInterestTagSelect = (
    selectedOptions: IOption[],
    options: IOption[],
    field: keyof CheckoutOptionsPageModel
  ) => {
    return (
      <CreatableSelect
        isClearable={true}
        isMulti={false}
        options={options}
        selectedOptions={selectedOptions}
        onSingleChange={(newValue) => handleSelectChange(field, newValue)}
        className="payment-method-select"
        classPrefix="payment-method"
        error={optionsPage.hasError(field)}
      />
    );
  };

  const renderActionsContent = () => {
    return (
      <>
        <FlexContainer fxLayout="column" spacing={20}>
          <div>
            <FlexContainer fxLayout="column" spacing={10}>
              <CustomLabel>Add List to Contact</CustomLabel>
              {renderListInterestTagSelect(
                selectedContactOption(optionsPage.selectedList),
                listInterestTagStore.listOptions,
                "selectedList"
              )}
            </FlexContainer>
          </div>
          <div>
            <FlexContainer fxLayout="column" spacing={10}>
              <CustomLabel>Add Tag to Contact</CustomLabel>
              {renderListInterestTagSelect(
                selectedContactOption(optionsPage.selectedTag),
                listInterestTagStore.tagOptions,
                "selectedTag"
              )}
            </FlexContainer>
          </div>
        </FlexContainer>
      </>
    );
  };

  const togglePopUp = () => {
    handleInputChange("isAddPayeePopup", !optionsPage.isAddPayeePopup);
  };

  // const renderFundingContent = () => {
  //     return (
  //         <FlexContainer className="label-description-row">
  //             <LabelCell>
  //                 <CustomLabel className="standard-lh">Payable</CustomLabel>
  //             </LabelCell>
  //             <DescriptionCell>
  //                 <FlexContainer
  //                     alignItems="center"
  //                     spacing={10}
  //                     className="clickable"
  //                     style={{ cursor: 'not-allowed' }}
  //                 >
  //                     <img src={AddIconBlue} />
  //                     <CustomLabel color="#556BB8" className="clickable">
  //                         Add Payee
  //                     </CustomLabel>{' '}
  //                 </FlexContainer>
  //             </DescriptionCell>
  //         </FlexContainer>
  //     );
  // };

  const checkBoxInput = (
    key: keyof CheckoutPaymentPageModel,
    value: boolean,
    label: string,
    disabled: boolean
  ) => {
    return (
      <Input
        type="checkbox"
        value={value}
        onChange={(e) => handleInputChangeForMarketing(e.target.checked, key)}
        label={label}
        disabled={disabled || false}
      />
    );
  };

  const handleInputChangeForMarketing = <
    T extends keyof CheckoutPaymentPageModel,
  >(
    value: CheckoutPaymentPageModel[T],
    field: T
  ) => {
    const clonedData = new CheckoutPaymentPageModel(paymentPageData);
    if (field in formData) {
      clonedData.formData = new CheckoutPaymentFormModel({
        ...formData,
        [field]: value,
      });
    } else {
      clonedData[field] = value;
    }
    props.checkoutStore.updatePaymentPage(
      new CheckoutPaymentPageModel(clonedData)
    );
  };

  const radioButton = (
    value: boolean,
    fieldName: keyof CheckoutPaymentPageModel,
    label: string,
    updateValue: keyof typeof CheckOutPromoCodeSelectionType
  ) => {
    return (
      <Input
        value={value}
        id={`rdb_${fieldName}`}
        type="radio"
        label={label}
        onChange={() => handleInputChangeForMarketing(updateValue, fieldName)}
        className={"chkbox-no-margin"}
      />
    );
  };

  const renderPayeeModelContent = () => {
    return (
      <FlexContainer>
        <CustomLabel>Hellooo</CustomLabel>
      </FlexContainer>
    );
  };

  const singleSelect = (
    field: keyof CheckoutPaymentPageModel,
    selectedKey: string,
    className: string,
    options: IOption[],
    error: boolean,
    errorMessage: string
  ): React.ReactNode => {
    return (
      <SingleSelect
        selectedKey={selectedKey}
        hasError={error}
        searchable={false}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options || []]}
        errorMessage={errorMessage}
        onChange={(option: any) =>
          handleInputChangeForMarketing(option.value, field)
        }
        className={className}
      />
    );
  };

  const renderMarketingInputs = () => {
    return (
      <FlexContainer fxLayout="column" spacing={18}>
        {checkBoxInput(
          "showTimer",
          paymentPageData.showTimer,
          "Show Checkout timer",
          false
        )}
        {/* {checkBoxInput(
          "isPromotionCodes",
          paymentPageData.isPromotionCodes,
          "Allow Promotion Codes",
          false
        )}
        {paymentPageData.isPromotionCodes && (
          <div>
            <FlexContainer
              alignItems="center"
              spacing={50}
              className={`marketing-radio-btns ${(paymentPageData.promoCodeSelectionType === CheckOutPromoCodeSelectionType.SelectCodes && "marketing-radio-spacing") || (paymentPageData.promoCodeSelectionType === CheckOutPromoCodeSelectionType.ApplyCode && "marketing-radio-spacing")}`}
            >
              {radioButton(
                paymentPageData.promoCodeSelectionType ===
                  CheckOutPromoCodeSelectionType.AllActiveCodes,
                "promoCodeSelectionType",
                "All Active Codes",
                CheckOutPromoCodeSelectionType.AllActiveCodes
              )}
              {radioButton(
                paymentPageData.promoCodeSelectionType ===
                  CheckOutPromoCodeSelectionType.SelectCodes,
                "promoCodeSelectionType",
                "Select Codes",
                CheckOutPromoCodeSelectionType.SelectCodes
              )}
              {radioButton(
                paymentPageData.promoCodeSelectionType ===
                  CheckOutPromoCodeSelectionType.ApplyCode,
                "promoCodeSelectionType",
                "Apply Code",
                CheckOutPromoCodeSelectionType.ApplyCode
              )}
            </FlexContainer>
            {paymentPageData.promoCodeSelectionType ===
              CheckOutPromoCodeSelectionType.SelectCodes && (
              <SelectCreateable
                isMulti={true}
                options={samplePromoCodes}
                selectedOptions={selectedPromoCodes()}
                // onChange={(newValue) => console.log(newValue)}
                className="chkout-pay-select"
                classPrefix="chkout-pay"
              />
            )}
            {paymentPageData.promoCodeSelectionType ===
              CheckOutPromoCodeSelectionType.ApplyCode &&
              singleSelect(
                "applyCodeValue",
                paymentPageData.applyCodeValue,
                "moneyback-chkout",
                [],
                false,
                ""
              )}
          </div>
        )}
        {checkBoxInput(
          "isTestimonial",
          paymentPageData.isTestimonial,
          "Show Testimonial Banner",
          false
        )}
        {paymentPageData.isTestimonial && (
          <SelectCreateable
            isMulti={true}
            options={samplePromoCodes}
            selectedOptions={selectedPromoCodes()}
            // onChange={(newValue) => console.log(newValue)}
            className="chkout-pay-select"
            classPrefix="chkout-pay"
          />
        )}
        {checkBoxInput(
          "isMoneyBack",
          paymentPageData.isMoneyBack,
          "Show Guarantee Banner",
          false
        )}
        {paymentPageData.isMoneyBack &&
          singleSelect(
            "moneyBackDuration",
            paymentPageData.moneyBackDuration,
            "moneyback-chkout",
            [],
            false,
            ""
          )} */}
      </FlexContainer>
    );
  };

  const renderMarketingSection = () => {
    return (
      <CollapsiblePanel
        title="Marketing"
        className="checkout-labels chkout-label-description"
      >
        <CustomLabel className="marketing-soft-text">
          Add available active options below from Payment Settings.
        </CustomLabel>
        {renderMarketingInputs()}
      </CollapsiblePanel>
    );
  };

  return (
    <CheckoutPaymentWrapper>
      <ProductDetailWrapper>
        <FlexContainer>
          <CheckoutDetailSection>
            <ItemHeader header="Options" />
            <CollapsiblePanel title="Branding" className="checkout-labels">
              <CheckoutBrandingSection
                onChange={(
                  field: keyof CheckoutOptionsPageModel,
                  value: CheckoutBrandingModel
                ) => {
                  handleInputChange(field, value);
                }}
                checkoutStore={props.checkoutStore}
              />
            </CollapsiblePanel>
            {renderMarketingSection()}
            <CollapsiblePanel title="Actions" className="checkout-labels">
              {renderActionsContent()}
            </CollapsiblePanel>
            {/* <CollapsiblePanel title="Funding" isShowLockIcon className="checkout-labels">
                          {renderFundingContent()}
                      </CollapsiblePanel> */}
            {optionsPage.isAddPayeePopup ? (
              <ModalComponent
                title="Payees"
                titleFontSize="22px"
                bodyPadding="50px 61px"
                showFooter={true}
                minHeight="440px"
                wrapperWidth="750px"
                bodyBackgroundColor="#fefefe"
                footerBackground="#F4F7FB"
                footerHeight="auto"
                onClose={() => togglePopUp()}
                footerContent={
                  <FlexContainer
                    alignItems="center"
                    justifyContent="center"
                    spacing={15}
                  >
                    <SapphireBlueButton
                      minWidth="120px"
                      height="40px"
                      onClick={() => togglePopUp()}
                    >
                      Close
                    </SapphireBlueButton>
                    <SapphireBlueButton
                      primary
                      minWidth="120px"
                      height="40px"
                      onClick={() => togglePopUp()}
                    >
                      Save
                    </SapphireBlueButton>
                  </FlexContainer>
                }
              >
                {renderPayeeModelContent()}
              </ModalComponent>
            ) : (
              ""
            )}
          </CheckoutDetailSection>
          <CheckoutIFrame checkoutStore={props.checkoutStore} />
        </FlexContainer>
      </ProductDetailWrapper>
    </CheckoutPaymentWrapper>
  );
};

export default withRouter(observer(CheckOutOptionsPage));
